import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';

import { 
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface ProductWithImages {
  id: string;
  productName: string;
  itemSpec: string;
  images: string[];
  quantity: number;
  sku: string;
}

interface ProductImageGalleryProps {
  order: any;
  onLoad?: () => void; // Add the onLoad callback prop
}

// Helper function to parse JSON string of images safely
const safeParseImages = (imageString: string): string[] => {
  if (!imageString) return [];
  
  try {
    const images = JSON.parse(imageString);
    return Array.isArray(images) ? images : [];
  } catch (e) {
    console.error('Error parsing image JSON:', e);
    return [];
  }
};

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({ order, onLoad }) => {
  // State for storing processed products with images
  const [products, setProducts] = useState<ProductWithImages[]>([]);
  // State for tracking active image index for each product
  const [activeIndices, setActiveIndices] = useState<Record<string, number>>({});
  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  // Track loaded images
  const [loadedImages, setLoadedImages] = useState<Record<string, number>>({});
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  // Track total expected images
  const [totalExpectedImages, setTotalExpectedImages] = useState(0);

  // Handle image load event
  const handleImageLoad = useCallback((productId: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1
    }));
  }, []);

  // Process order data into gallery items
  useEffect(() => {
    if (!order || !order.items) {
      setProducts([]);
      setIsLoading(false);
      // Reset loaded image tracking when order changes
      setLoadedImages({});
      setAllImagesLoaded(false);
      setTotalExpectedImages(0);
      return;
    }
    
    console.log('ProductImageGallery: Processing order with', order.items.length, 'items');
    
    // Use a map to track unique SKUs to prevent duplicates
    const uniqueProducts = new Map<string, any>();
    
    // First pass: collect unique products by SKU
    order.items.forEach((item: any) => {
      // Skip items without images
      const hasMainImage = !!item.imageUrl;
      const hasAdditionalImages = item.original && item.original.all_images;
      
      if (!hasMainImage && !hasAdditionalImages) return;
      
      // Use SKU as the key for deduplication
      // If SKU is empty, create a unique key from product name and variant
      const key = item.sku || `${item.productName}_${item.itemSpec}`;
      
      if (!key) return; // Skip items without any identifiable key
      
      // Only add if not already in the map, or if it has more images than the existing one
      if (!uniqueProducts.has(key)) {
        uniqueProducts.set(key, item);
      } else {
        // Check if this item has more images than the one already in the map
        const existingItem = uniqueProducts.get(key);
        const existingImages = safeParseImages(existingItem.original?.all_images);
        const newImages = safeParseImages(item.original?.all_images);
        
        // Replace if new item has more images
        if (newImages.length > existingImages.length) {
          uniqueProducts.set(key, item);
        }
      }
    });
    
    console.log(`Identified ${uniqueProducts.size} unique products after deduplication`);
    
    // Second pass: transform to products with images
    const productsWithImages = Array.from(uniqueProducts.values())
      .map((item: any, index: number) => {
        // Start with an empty array of images
        let images: string[] = [];
        
        // Add the main image if it exists
        if (item.imageUrl) {
          images.push(item.imageUrl);
        }
        
        // Try to parse additional images if available
        if (item.original && item.original.all_images) {
          try {
            const parsedImages = safeParseImages(item.original.all_images);
            
            // If we successfully parsed additional images, use them
            if (parsedImages && parsedImages.length > 0) {
              console.log(`Found ${parsedImages.length} images for ${item.productName}`);
              images = parsedImages;
            }
          } catch (e) {
            console.error('Error parsing images for item:', item.id, e);
          }
        }
        
        return {
          id: `${order.orderId}_${item.id}_${index}`,
          productName: item.productName,
          itemSpec: item.itemSpec,
          images: images,
          quantity: item.quantity,
          sku: item.sku
        };
      });
    
    console.log('ProductImageGallery: Processed', productsWithImages.length, 'unique products with images');
    
    // Initialize active indices for each product
    const indices: Record<string, number> = {};
    productsWithImages.forEach(product => {
      indices[product.id] = 0;
    });
    
    // Calculate total expected images for loading tracking
    let totalImages = 0;
    productsWithImages.forEach(product => {
      totalImages += product.images.length;
    });
    setTotalExpectedImages(totalImages);
    
    setProducts(productsWithImages);
    setActiveIndices(indices);
    setIsLoading(false);
    
    // Reset loaded image tracking when products change
    setLoadedImages({});
    setAllImagesLoaded(false);
  }, [order]);

  // Check if all images are loaded and call onLoad callback
  useEffect(() => {
    if (!isLoading && products.length > 0) {
      // Count total loaded images
      const totalLoaded = Object.values(loadedImages).reduce((sum, count) => sum + count, 0);
      
      // If all expected images are loaded and we haven't triggered the callback yet
      if (totalLoaded >= totalExpectedImages && !allImagesLoaded && totalExpectedImages > 0) {
        console.log(`ProductImageGallery: All ${totalLoaded}/${totalExpectedImages} images loaded`);
        setAllImagesLoaded(true);
        
        // Call the onLoad callback
        if (onLoad) {
          setTimeout(() => {
            onLoad();
          }, 50); // Small delay to ensure DOM is updated
        }
      }
    } else if (!isLoading && products.length === 0) {
      // No products with images, still need to trigger onLoad
      if (!allImagesLoaded && onLoad) {
        setAllImagesLoaded(true);
        onLoad();
      }
    }
  }, [loadedImages, products, isLoading, allImagesLoaded, onLoad, totalExpectedImages]);

  // Image navigation handlers
  const prevImage = useCallback((productId: string) => {
    setActiveIndices(prev => {
      const product = products.find(p => p.id === productId);
      if (!product || !product.images.length) return prev;
      
      const currentIndex = prev[productId] || 0;
      const prevIndex = (currentIndex - 1 + product.images.length) % product.images.length;
      return { ...prev, [productId]: prevIndex };
    });
  }, [products]);

  const nextImage = useCallback((productId: string) => {
    setActiveIndices(prev => {
      const product = products.find(p => p.id === productId);
      if (!product || !product.images.length) return prev;
      
      const currentIndex = prev[productId] || 0;
      const nextIndex = (currentIndex + 1) % product.images.length;
      return { ...prev, [productId]: nextIndex };
    });
  }, [products]);

  // Modal handlers
  const openModal = (image: string) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  // If loading
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // If no products with images
  if (products.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          No product images available
        </Typography>
      </Box>
    );
  }

  // Calculate how many rows we might need based on products count
  // Assuming 3 items per row on desktop, 2 on tablet, 1 on mobile
  // Each product card is roughly 350px high with all elements
  const estimatedHeight = Math.min(
    // Limit to max height on screen
    window.innerHeight * 0.6,
    // Calculate based on products (use ceil to account for partial rows)
    Math.ceil(products.length / 3) * 350
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
        Product Gallery ({products.length} Products)
      </Typography>
      
      {/* Use a Box with fixed height and overflow-y: auto for vertical scrolling */}
      <Box sx={{ 
        height: products.length > 3 ? `${estimatedHeight}px` : 'auto', // Fixed height if more than 3 products
        maxHeight: '60vh', // Safety cap at 60% of viewport height
        overflowY: 'auto',
        // Add styling for scrollbar to make it more visible
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: '#f0f0f0',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#bdbdbd',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#9e9e9e',
        },
        // Add padding for better appearance
        pr: 1,
        pb: 2,
        // Add border to clearly show scrollable area
        border: products.length > 3 ? '1px solid #e0e0e0' : 'none',
        borderRadius: '4px',
      }}>
        <Grid container spacing={2}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    '& .imageControls': {
                      opacity: 1
                    }
                  }
                }}
              >
                {/* Image container */}
                <Box
                  sx={{
                    position: 'relative',
                    height: 200,
                    bgcolor: 'background.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {product.images.length > 0 && (
                    <img
                      src={product.images[activeIndices[product.id] || 0]}
                      alt={product.productName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        cursor: 'pointer'
                      }}
                      onClick={() => openModal(product.images[activeIndices[product.id] || 0])}
                      loading="lazy"
                      onLoad={() => handleImageLoad(product.id)}
                    />
                  )}
                  
                  {/* Image navigation - only show if there's more than one image */}
                  {product.images.length > 1 && (
                    <Box
                      className="imageControls"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        p: 1
                      }}
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          prevImage(product.id);
                        }}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.3)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          nextImage(product.id);
                        }}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.3)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  )}
                  
                  {/* Zoom button */}
                  <Tooltip title="View full size">
                    <IconButton
                      size="small"
                      onClick={() => openModal(product.images[activeIndices[product.id] || 0])}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  {/* Image counter badge - only show if there's more than one image */}
                  {product.images.length > 1 && (
                    <Chip
                      label={`${(activeIndices[product.id] || 0) + 1}/${product.images.length}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  )}
                </Box>
                
                {/* Thumbnails */}
                {product.images.length > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      overflowX: 'auto',
                      p: 1,
                      bgcolor: 'background.paper',
                      gap: 0.5,
                      '&::-webkit-scrollbar': {
                        height: 6,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: 3,
                      }
                    }}
                  >
                    {product.images.map((image, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          cursor: 'pointer',
                          border: (activeIndices[product.id] || 0) === idx
                            ? '2px solid #1976d2'
                            : '2px solid transparent',
                          borderRadius: '2px',
                          overflow: 'hidden',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        onClick={() => setActiveIndices(prev => ({
                          ...prev,
                          [product.id]: idx
                        }))}
                      >
                        <img
                          src={image}
                          alt={`${product.productName} thumbnail ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          loading="lazy"
                          onLoad={() => handleImageLoad(product.id)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
                
                {/* Product info */}
                <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    noWrap 
                    title={product.productName}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {product.productName}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    noWrap
                    title={product.itemSpec}
                  >
                    {product.itemSpec}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      SKU: {product.sku || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Qty: {product.quantity}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      
      {/* Full size image modal */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 1, position: 'relative' }}>
          <IconButton
            onClick={closeModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                minHeight: '60vh',
                overflow: 'hidden'
              }}
            >
              <img
                src={selectedImage}
                alt="Full size product"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
                onLoad={() => {}} // No need to track modal image loads
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductImageGallery;